<template>
	<div class="ticket-category-form">
		<TranslateableField
			typeName="Reduction2"
			fieldName="title"
			v-model="model.fields.title"
			:locales="showAllLocales ? locales.map(l => l.code) : undefined"
			:requiredLocales="requiredTranslationLocaleCodes"
		/>
		<IfFeatureFlag flag="FEATURE_TRADE_HUB">
			<div v-if="featureEnabled('trade-hub') && featureEnabled('trade-hub-additional-settings')">
				<FieldSet class="mt-8" :label="$t('text.reductionAdditionalSettings')">
					<Field
						typeName="Reduction2"
						fieldName="ageGroupEnabled"
						v-model="model.fields.ageGroupEnabled"
					/>
					<div style="display: flex; gap: 20px;">
						<Field
							v-if="model.fields.ageGroupEnabled?.de"
							typeName="Reduction2"
							fieldName="minAge"
							v-model="model.fields.minAge"
							:min="0"
							:max="120"
							:step="1"
							:errorMessages="!hasMinNotExceedingMax ? [$t('text.minAgeExceedsMaxAgeError')] : []"
						/>
						<Field
							v-if="model.fields.ageGroupEnabled?.de"
							typeName="Reduction2"
							fieldName="maxAge"
							v-model="model.fields.maxAge"
							:min="0"
							:max="120"
							:step="1"
							:errorMessages="!hasMinNotExceedingMax ? [$t('text.minAgeExceedsMaxAgeError')] : []"
						/>
					</div>
					<Field
						typeName="Reduction2"
						fieldName="freeTourGuide"
						v-model="model.fields.freeTourGuide"
						:errorMessages="!hasValidFreeTourGuideAndFlatRate ? [$t('text.freeTourGuideAndFlatRateMutuallyExclusiveError')] : []"
					/>
					<Field
						typeName="Reduction2"
						fieldName="flatRate"
						v-model="model.fields.flatRate"
						:errorMessages="!hasValidFreeTourGuideAndFlatRate ? [$t('text.freeTourGuideAndFlatRateMutuallyExclusiveError')] : []"
					/>
				</FieldSet>
			</div>
		</IfFeatureFlag>

		<IfFeatureFlag flag="FEATURE_TRANSLATION_WIZARD">
			<TranslationWizardDialog ref="translationWizardDialog" :fields="translatableFields" :requiredLocales="requiredTranslationLocaleCodes" />
		</IfFeatureFlag>
	</div>
</template>

<script>
import Common from '@/mixins/Common.vue'
import IfFeatureFlag from '@/components/ifFeatureFlag/IfFeatureFlag.vue'
import TranslateableField from '@/components/fields/TranslateableField.vue'
import TranslationWizardDialog from '@/components/TranslationWizardDialog.vue'
import FieldSet from '../packageDesigner/FieldSet.vue'
import { Field } from '../../../../../shared/entry-decorators'
import ChildErrorDispatcher from '../packageDesigner/ChildErrorDispatcher.vue'

export default {
	name: 'TicketCategoryForm',
	mixins: [ Common, ChildErrorDispatcher ],
	components: { IfFeatureFlag, TranslateableField, TranslationWizardDialog, FieldSet },
	props: {
		modelValue: Object,
		loading: Boolean,
		showAllLocales: { type: Boolean, default: false },
		serviceProviderId: { type: String, default: null },
	},
	emits: ['update:modelValue', 'update:loading', 'saved', 'update:valid'],
	data() {
		return {}
	},
	computed: {
		model: {
			get() { return this.modelValue },
			set(value) { this.$emit('update:modelValue', value) },
		},
		isNew() {
			return !this.model?.sys?.id
		},
		translatableFields() {
			return [
				{ typeName: 'Reduction2', fieldName: 'title', value: this.model?.fields?.title },
			]
		},
		translationStatuses() {
			return this.getTranslationStatus2(this.translatableFields, 'model', this.requiredTranslationLocaleCodes)
		},
		requiredTranslationLocaleCodes() {
			return this.getRequiredTranslationLocaleCodes()
		},
		hasValidMinAge() {
			return !this.model?.fields?.ageGroupEnabled?.de ||
				(this.model?.fields?.minAge?.de !== null && this.model?.fields?.minAge?.de !== '')
		},
		hasValidMaxAge() {
			return !this.model?.fields?.ageGroupEnabled?.de ||
				(this.model?.fields?.maxAge?.de !== null && this.model?.fields?.maxAge?.de !== '')
		},
		hasNonNegativeAges() {
			if (!this.model?.fields?.ageGroupEnabled?.de) return true
			const min = this.model?.fields?.minAge?.de
			const max = this.model?.fields?.maxAge?.de
			const minOk = min === null || min === undefined || min === '' || Number(min) >= 0
			const maxOk = max === null || max === undefined || max === '' || Number(max) >= 0
			return minOk && maxOk
		},
		hasMinNotExceedingMax() {
			if (!this.model?.fields?.ageGroupEnabled?.de) return true
			const min = this.model?.fields?.minAge?.de
			const max = this.model?.fields?.maxAge?.de
			if (min === null || min === undefined || min === '') return true
			if (max === null || max === undefined || max === '') return true
			return Number(min) <= Number(max)
		},
		hasValidFreeTourGuideAndFlatRate() {
			// freeTourGuide and flatRate are mutually exclusive
			return this.model?.fields?.freeTourGuide?.de !== true || this.model?.fields?.flatRate?.de !== true
		},
		isValid() {
			const hasTitleForRequiredLocales = this.checkRequiredTranslationLocales(this.model?.fields?.title, this.requiredTranslationLocaleCodes)
			return hasTitleForRequiredLocales
				&& this.hasValidMinAge
				&& this.hasValidMaxAge
				&& this.hasNonNegativeAges
				&& this.hasMinNotExceedingMax
				&& this.hasValidFreeTourGuideAndFlatRate
		},
	},
	watch: {
		isValid: {
			immediate: true,
			handler(value) {
				this.$emit('update:valid', value)
			},
		},
	},
	methods: {
		openTranslationWizard() {
			this.$refs.translationWizardDialog?.open()
		},
		async loadTicketCategory(ticketCategoryId) {
			this.$emit('update:loading', true)
			try {
				const loadedTicketCategory = await this.$httpGet(`/content/entry/${ticketCategoryId}`)
				if (!loadedTicketCategory?.fields?.ageGroupEnabled) {
					loadedTicketCategory.fields.ageGroupEnabled = { de: false }
				}
				if (!loadedTicketCategory?.fields?.minAge) {
					loadedTicketCategory.fields.minAge = { de: null }
				}
				if (!loadedTicketCategory?.fields?.maxAge) {
					loadedTicketCategory.fields.maxAge = { de: null }
				}
				if (!loadedTicketCategory?.fields?.freeTourGuide) {
					loadedTicketCategory.fields.freeTourGuide = { de: false }
				}
				if (!loadedTicketCategory?.fields?.flatRate) {
					loadedTicketCategory.fields.flatRate = { de: false }
				}
				this.model = loadedTicketCategory
			}
			catch (error) {
				this.applicationError(this.$el, this.$t('text.ERROR'), error.response?.data?.error ?? error)
			}
			this.$emit('update:loading', false)
		},
		validate() {
			return this.isValid
		},
		async save() {
			if (this.model.fields.ageGroupEnabled?.de === false) {
				this.model.fields.minAge.de = null
				this.model.fields.maxAge.de = null
			}

			if (!this.validate()) {
				this.applicationError(this.$el, this.$t('text.ERROR'), this.$t('text.missingFieldsError'))
				return null
			}

			this.$emit('update:loading', true)

			const data = this.isNew ? {
				clientId: this.$store.state.selectedClient.sys.id,
				serviceProviderId: this.serviceProviderId ?? (this.userIsOperator ? undefined : this.$store.state.selectedServiceProvider.sys.id),
				itemType: 'reduction',
				name: this.model.fields.title,
				ageGroupEnabled: this.model.fields.ageGroupEnabled,
				minAge: this.model.fields.minAge,
				maxAge: this.model.fields.maxAge,
				freeTourGuide: this.model.fields.freeTourGuide,
				flatRate: this.model.fields.flatRate,
			} : {
				item: this.model,
				clientId: this.$store.state.selectedClient.sys.id,
			}

			try {
				let response = null
				if (this.isNew) {
					response = await this.$httpPost('/option', data)
				} else {
					response = await this.$httpPut('/option', data)
				}

				const upsertedTicketCategory = response?.option

				// Update client in store
				const res = await this.$httpGet(`/client/${this.$store.state.selectedClient.sys.id}`)
				await this.$store.commit('setSelectedClient', res.client)

				this.$emit('update:loading', false)
				this.$emit('saved', upsertedTicketCategory)
				return upsertedTicketCategory
			}
			catch (error) {
				this.$emit('update:loading', false)
				this.applicationError(this.$el, this.$t('text.ERROR'), error.response?.data?.error ?? error)
				return null
			}
		},
	},
}
</script>

<style scoped>
.ticket-category-form {
	padding: 16px 0;
	width: 100%;
}
.translation-button-container {
	display: flex;
	align-items: center;
	gap: 16px;
}
.translation-status {
	display: flex;
	gap: 8px;
}
.locale-status {
	padding: 2px 6px;
	border-radius: 4px;
	font-size: 12px;
	background-color: #ffcdd2;
	color: #c62828;
}
.locale-status.complete {
	background-color: #c8e6c9;
	color: #2e7d32;
}
</style>
